import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormGroup, FormControl, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable, of, switchMap } from 'rxjs';

import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChipModule } from 'primeng/chip';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

import { Store } from '@ngxs/store';
import { RemoveOrganization } from '@stores/organizations/organizations.actions';

import { Organization } from '@features/organizations/models/organization.model';
import { sanitizeToSlug } from '@core/utils/general.utils';
import { OrganizationsService } from '@features/organizations/services/organizations.service';
import { Games } from '@core/enums/games.enum';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { markAllAsDirty } from '@core/utils/form.utils';
import { PrimeNgSettings } from '@core/static/primeng.static';
import { ErrorCodes } from '@core/enums/errors.enum';

@Component({
  selector: 'tr-organization-form',
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    AutoCompleteModule,
    FormTextInputComponent,
    ChipModule,
    InputGroupModule,
    InputGroupAddonModule,
    DragDropModule,
  ],
  templateUrl: './Form.component.html',
  styleUrl: './Form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormComponent implements OnInit {
  private messageService = inject(MessageService);
  private orgService = inject(OrganizationsService);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig);
  private store = inject(Store);

  organizationForm: FormGroup;
  submitting: boolean = false;
  deleting: boolean = false;
  showDeleteConfirm: boolean = false;
  deleteConfirmText: string = '';

  get isEditMode(): boolean {
    return !!this.dialogConfig.data?.Organization?.id;
  }

  private modifiedSlug: boolean = false;

  gameSuggestions = Games;
  locationName = new FormControl('');

  constructor() {
    // Initialize form group
    this.organizationForm = new FormGroup({
      id: new FormControl(''),
      name: new FormControl('', {
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-Z0-9- ]+$'),
        ],
      }),
      description: new FormControl('', { validators: [Validators.maxLength(500)], updateOn: 'submit' }),
      slug: new FormControl('', {
        validators: [
          Validators.required,
          Validators.maxLength(50),
          Validators.minLength(3),
          Validators.pattern('^[a-z0-9-]+$'),
        ],
      }),
      game: new FormControl('', {
        validators: [Validators.minLength(3), Validators.maxLength(50), Validators.pattern('^[a-zA-Z0-9- ]+$')],
      }),
      locations: new FormControl([]),
    });
  }

  ngOnInit(): void {
    if (this.dialogConfig.data?.Organization) {
      const orgInfo = this.dialogConfig.data.Organization;

      this.organizationForm.setValue({
        id: orgInfo.id,
        name: orgInfo.name,
        description: orgInfo.description ?? '',
        slug: orgInfo.slug,
        game: orgInfo.game ?? '',
        locations: orgInfo.locations ?? [],
      });
    }

    this.setupForm();
  }

  getControl(name: string): FormControl {
    return this.organizationForm.get(name) as FormControl;
  }

  private setupForm() {
    if (this.organizationForm.value.id === '') {
      this.organizationForm.get('name')?.valueChanges.subscribe((nameValue: string) => {
        // Convert the name to the sanitized slug
        const sanitized = sanitizeToSlug(nameValue);

        if (this.modifiedSlug === false) {
          this.organizationForm.get('slug')?.setValue(sanitized, { emitEvent: false });
        }
      });
    }

    this.organizationForm.get('slug')?.valueChanges.subscribe((urlValue: string) => {
      // If user types in the slug field, sanitize it.
      const sanitized = sanitizeToSlug(urlValue);

      // If the user typed an uppercase or invalid char, correct it automatically
      if (sanitized !== urlValue) {
        this.organizationForm.get('slug')?.setValue(sanitized, { emitEvent: false });
      }

      this.modifiedSlug = true;

      if (!sanitized) {
        this.modifiedSlug = false;
      }
    });
  }

  searchGames(event: any) {
    const query = event.query.toLowerCase();
    this.gameSuggestions = Games.filter((game) => game.toLowerCase().includes(query));
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  addLocation(location: string) {
    location = location.trim();
    if (!location) return;

    const currentLocations = this.organizationForm.get('locations')!.value || [];
    currentLocations.push(location);
    this.organizationForm.get('locations')!.setValue(currentLocations);

    this.locationName.setValue('');
  }

  removeLocation(index: number): void {
    const currentLocations = this.organizationForm.get('locations')!.value || [];
    currentLocations.splice(index, 1);
    this.organizationForm.get('locations')!.setValue(currentLocations);
  }

  dropLocation(event: CdkDragDrop<string[]>) {
    const currentLocations: string[] = this.organizationForm.get('locations')!.value || [];
    moveItemInArray(currentLocations, event.previousIndex, event.currentIndex);
    this.organizationForm.get('locations')?.setValue(currentLocations);
  }

  locationDraggedIndex: number | null = null;

  onLocationDragStart(index: number) {
    this.locationDraggedIndex = index;
  }

  onLocationDragEnd() {
    this.locationDraggedIndex = null;
  }

  onSubmit() {
    markAllAsDirty(this.organizationForm);

    if (this.organizationForm.valid) {
      this.submitting = true;

      const formValue = this.organizationForm.value;

      if (this.locationName.value) {
        formValue.locations.push(this.locationName.value);
      }

      const data = {
        orgId: formValue.id ?? '',
        name: formValue.name,
        description: formValue.description,
        slug: formValue.slug,
        game: formValue.game,
        locations: formValue.locations,
      };

      const name = formValue.name.trim();
      const slug = formValue.slug.trim();

      this.orgService
        .checkOrganizationConflicts(name, slug, formValue.id)
        .pipe(
          switchMap(({ nameExists, slugExists }) => {
            if (nameExists) {
              this.organizationForm.get('name')?.setErrors({ exists: 'Organization name is already taken' });
              this.submitting = false;
              this.cdr.detectChanges();
              return of(null);
            }

            if (slugExists) {
              this.organizationForm.get('slug')?.setErrors({ exists: 'Slug is already taken' });
              this.submitting = false;
              this.cdr.detectChanges();
              return of(null);
            }

            return this.save(data);
          })
        )
        .subscribe({
          next: (result) => {
            if (result) {
              this.messageService.add({
                severity: 'success',
                key: 'main-toast',
                summary: 'Saved',
                detail: `Successfully saved your organization ${data.name}`,
                life: PrimeNgSettings.MessageLife,
              });
              this.dialogRef.close(result);
            }
          },
          error: (error) => {
            this.submitting = false;
            this.cdr.detectChanges();

            if (error.code === ErrorCodes.OrganizationMaxAllowed) {
              this.messageService.add({
                severity: 'warn',
                key: 'main-toast',
                summary: 'Maximum reached',
                detail: 'Sorry, you have reached the maximum number of organizations you are allowed to create',
                life: PrimeNgSettings.MessageLife,
              });
            } else {
              this.messageService.add({
                severity: 'error',
                key: 'main-toast',
                summary: 'Failed',
                detail: `Failed to save the organization, please try again`,
                life: PrimeNgSettings.MessageLife,
              });
            }
          },
        });
    }
  }

  save(data: any): Observable<Organization | null> {
    return this.orgService.save(data);
  }

  startDeleteConfirm() {
    this.showDeleteConfirm = true;
    this.deleteConfirmText = '';
    this.cdr.detectChanges();
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
    this.deleteConfirmText = '';
    this.cdr.detectChanges();
  }

  confirmDelete() {
    if (this.deleteConfirmText !== 'DELETE') return;

    const orgId = this.organizationForm.value.id;
    this.deleting = true;
    this.cdr.detectChanges();

    this.orgService.deleteOrganization(orgId).subscribe({
      next: () => {
        this.store.dispatch(new RemoveOrganization(orgId));
        this.messageService.add({
          severity: 'success',
          key: 'main-toast',
          summary: 'Deleted',
          detail: 'Organization has been permanently deleted',
          life: PrimeNgSettings.MessageLife,
        });
        this.dialogRef.close({ deleted: true });
      },
      error: () => {
        this.deleting = false;
        this.messageService.add({
          severity: 'error',
          key: 'main-toast',
          summary: 'Failed',
          detail: 'Failed to delete the organization, please try again',
          life: PrimeNgSettings.MessageLife,
        });
        this.cdr.detectChanges();
      },
    });
  }
}
